import { mat4, vec2, vec3, vec4 } from "gl-matrix";
import { EaseFunction, linear } from "./interpolation";
import { FontName, Fonts, LoadedFont } from "./loader";
import { Text } from "./Text";
import { TextShader } from "./rendering/TextShader";
import { isVec2, isVec3, lerpVec3Eased } from "./vector";
import { Camera, Computed, Light, SceneLighting, ShaderCamera } from "./common";
import { CameraPath } from "./CameraPath";
import {
  HorizontalTextParams,
  TextAlignment,
  TextSpec,
  VerticalTextParams,
} from "./sdfText";
import { clamp01 } from "./math";

export interface Transition1D {
  from: number;
  to: number;
}

interface SceneTransitionContext {
  nowInSeconds: number;
  sceneNowInSeconds: number;
  sceneNowInBeats: number;
  sceneProgress: number;
}

export type SceneComputedFloat = (ctx: SceneTransitionContext) => number;
export type SceneComputedVec4 = (ctx: SceneTransitionContext) => vec4;

interface SequenceTransitionContext {
  sceneContext: SceneTransitionContext;
  partProgress: number;
}

export type SequenceComputedFloat = (ctx: SequenceTransitionContext) => number;
export type SequenceComputedVec3 = (ctx: SequenceTransitionContext) => vec3;
export type SequenceComputedVec4 = (ctx: SequenceTransitionContext) => vec4;

export interface Transition3D {
  from: vec3;
  to: vec3;
}

export interface Transition2D {
  from: vec2;
  to: vec2;
  ease?: EaseFunction;
}

export interface CameraTransition {
  position: vec3 | Transition3D;
  target: vec3 | Transition3D;
  rotationY?: SequenceComputedFloat;
  ease?: EaseFunction;
}

// A piece of text shown during a scene
export type SceneText = {
  font: FontName;
  text: string;
  position: vec2 | Transition2D;
  scale?: vec2;
  timing?: {
    enter: number;
    exit: number;
  };
  params?: TextParams;
} & (
  | ({
      type: "horizontal";
    } & HorizontalTextParams)
  | ({
      type: "vertical";
    } & VerticalTextParams)
);
export interface TextParams {
  waviness?: number;
  distortion?: number;
  color?: vec3;
}

// Each scene can contain a sequence, which consists of parts
// Each part can define attributes such as camera position, text, etc.
// which are interpolated between the start and end of the part
export interface Sequence {
  scene: Scene;
  parts: SequencePart[];
}

export interface SequencePartParams {
  /**
   * Length in beats
   */
  length: number;
  assertStart?: number;
  camera: CameraTransition | CameraPath;
  text?: SceneText[];
  lighting?: SceneLighting;
  fade?: SequenceComputedVec4;
  backgroundColor?: SequenceComputedVec3;
  shader?: ShaderName;
  sceneId?: number;
}

export interface SequencePart extends SequencePartParams {
  start: number;
  sequence: Sequence;
  previous?: SequencePart;
  next?: SequencePart;
}

export type ShaderName =
  | "intro"
  | "main"
  | "macaroniFactory"
  | "macaroniMachine"
  | "macaroniBox"
  | "tesseract"
  | "lasaga"
  | "outro";

interface SceneParams {
  name?: string;
  /**
   * Length in beats
   */
  length: number;
  sceneId?: number;
  shader: ShaderName;
  text?: SceneText[];
  lighting?: Computed<SceneLighting, SceneTransitionContext>;
  variables?: SceneComputedVec4;
  variables2?: SceneComputedVec4;
  effects?: {
    enable?: ["vhs"];
  };
}

export interface Scene extends SceneParams {
  start: number;
  previous?: Scene;
  next?: Scene;
  sequence?: Sequence;
  then: (scene: SceneParams) => Scene;
  thenScene: (scene: Scene) => Scene;
  addSequence: (sequence: SequencePartParams[]) => Scene;
}

const LIGHTING_DEFAULT: SceneLighting = {
  ambient: [0.2, 0.2, 0.2],
  light1: {
    position: [0, 0, 0],
    intensity: [0.5, 0.55, 0.6],
  },
  cameraLightIntensity: [0.5, 0.55, 0.6],
  fogColor: [0.1098039216, 0.1647058824, 0.3882352941],
  fogDensity: 1.5,
};

export function createScene(data: SceneParams): Scene {
  return {
    ...data,
    start: 0,
    then(sceneParams: SceneParams) {
      const scene = createScene(sceneParams);
      this.next = scene;
      scene.previous = this;
      scene.start = this.start + this.length;
      return scene;
    },
    thenScene(scene: Scene) {
      // Traverse the linked list to find the first scene
      let sceneIter = scene;
      while (sceneIter.previous) {
        sceneIter = sceneIter.previous;
      }

      this.next = sceneIter;
      sceneIter.previous = this;
      sceneIter.start = this.start + this.length;

      // Iterate all following scenes
      let currentScene: Scene | undefined = sceneIter;
      let start = sceneIter.start;
      while (currentScene) {
        currentScene.start = start;
        start += currentScene.length;
        currentScene = currentScene.next;
      }

      return scene;
    },
    addSequence(partParams: SequencePartParams[]) {
      const sequence: Sequence = { parts: [], scene: this };

      let start = 0;
      let previous: SequencePart | undefined;

      for (const part of partParams) {
        const partStart = start;
        const partEnd = start + part.length;

        if (part.assertStart && partStart !== part.assertStart) {
          throw new Error(
            `Sequence part starts at ${partStart} but should start at ${part.assertStart}`,
          );
        }

        start = partEnd;

        const partData: SequencePart = {
          ...part,
          start: partStart,
          sequence,
        };

        if (previous) {
          previous.next = partData;
          partData.previous = previous;
        }

        sequence.parts.push(partData);
        previous = partData;
      }

      this.sequence = sequence;
      return this;
    },
  };
}

interface TextInstant {
  text: Text;
  opacity: number;
  params?: TextParams;
}

// An instant state is defined for every time value in a sequence
export interface InstantState {
  shader: ShaderName;

  // Absolute time in seconds as measured by the audio context
  now: number;
  // Time in seconds since the start of the current scene
  sceneNow: number;
  // Absolute time in fractional beats
  beat: number;
  nowInSceneBeats: number;
  // Progress through the current scene, as a fraction
  // If there's no next scene, this is infinity
  sceneProgress: number;

  name: string;

  // Scene to play
  sceneId: number;

  camera: Camera;

  texts: TextInstant[];

  lighting: SceneLighting;
  sceneVariables: vec4;
  sceneVariables2?: vec4;
  fade: vec4;
  backgroundColor: vec3;

  effects?: {
    enable?: ["vhs"];
  };
}

export const DEFAULT_STATE: InstantState = {
  shader: "main",
  now: 0,
  sceneNow: 0,
  beat: 0,
  nowInSceneBeats: 0,
  sceneProgress: 0,
  name: "unnamed",
  sceneId: 0,
  camera: {
    position: [0, 0, 0],
    target: [0, 0, 0],
    rotationY: 0,
  },
  texts: [],
  lighting: LIGHTING_DEFAULT,
  sceneVariables: [0, 0, 0, 0],
  fade: [0, 0, 0, 0],
  backgroundColor: [0, 0, 0],
};

export interface SequencerConfig {
  bpm: number;
  initialScene: Scene;
}

function getTransition3DFrom(transition: Transition3D | vec3): vec3 {
  return isVec3(transition) ? transition : transition.from;
}

function getTransition3DTarget(
  transition: Transition3D | vec3,
): vec3 | undefined {
  return "to" in transition ? transition.to : undefined;
}

function computeTransition3D(
  transitionOrPosition: Transition3D | vec3,
  fraction: number,
  easeFunction: EaseFunction = linear,
): vec3 {
  const from = getTransition3DFrom(transitionOrPosition);
  const to = getTransition3DTarget(transitionOrPosition);
  return to ? lerpVec3Eased(easeFunction, from, to, fraction) : from;
}

function computeTransition2D(
  transitionOrPosition: Transition2D | vec2,
  fraction: number,
): vec2 {
  const from = isVec2(transitionOrPosition)
    ? transitionOrPosition
    : transitionOrPosition.from;
  const to = "to" in transitionOrPosition ? transitionOrPosition.to : undefined;
  const easing =
    "ease" in transitionOrPosition && transitionOrPosition.ease
      ? transitionOrPosition.ease
      : linear;
  return to ? vec2.lerp(vec2.create(), from, to, easing(fraction)) : from;
}

function computeSceneVariables(
  computeVariables: SceneComputedVec4 | undefined,
  ctx: SceneTransitionContext,
): vec4 {
  if (!computeVariables) {
    return [0, 0, 0, 0];
  }

  return computeVariables(ctx);
}

function computeCamera(
  camera: CameraTransition | CameraPath,
  ctx: SequenceTransitionContext,
): Camera {
  if ("keyframes" in camera) {
    return camera.evaluate(ctx.partProgress);
  } else {
    const position = computeTransition3D(
      camera.position,
      ctx.partProgress,
      camera.ease,
    );
    const target = computeTransition3D(
      camera.target,
      ctx.partProgress,
      camera.ease,
    );

    const rotationY = camera.rotationY ? camera.rotationY(ctx) : 0;

    return {
      position,
      target,
      rotationY,
    };
  }
}

// The idea of the sequencer is to be effectively stateless and deterministic
export class Sequencer {
  /**
   * This is just an optimization; if scenes are not requested in order,
   * we need to traverse the linked list from the beginning to find the current scene
   */
  private currentScene: Scene;
  private currentSequencePart: SequencePart | undefined;

  private sceneTexts = new WeakMap<SceneText, Text>();

  private readonly BEAT_LENGTH = 60 / this.config.bpm;

  constructor(private readonly config: SequencerConfig) {
    this.currentScene = config.initialScene;
  }

  preprocess(shader: TextShader, fonts: Fonts) {
    let scene: Scene | undefined = this.config.initialScene;

    function sceneTextToText(sceneText: SceneText, font: LoadedFont): Text {
      if (sceneText.type === "horizontal") {
        return Text.create(shader, font, {
          type: "horizontal",
          text: sceneText.text,
          align: sceneText.align ?? "center",
        });
      } else {
        return Text.create(shader, font, {
          type: "vertical",
          text: sceneText.text,
          verticalSpacing: sceneText.verticalSpacing,
          spaceHeight: sceneText.spaceHeight,
        });
      }
    }

    while (scene) {
      if (scene.text) {
        for (const sceneText of scene.text) {
          const font = fonts[sceneText.font];
          const text = sceneTextToText(sceneText, font);
          text.transformation.setScale([font.scale, font.scale, 1]);
          this.sceneTexts.set(sceneText, text);
        }
      }

      // Process every part of the sequence
      if (scene.sequence) {
        for (const part of scene.sequence.parts) {
          if (part.text) {
            for (const partText of part.text) {
              const font = fonts[partText.font];
              const text = sceneTextToText(partText, font);
              text.transformation.setScale([font.scale, font.scale, 1]);
              this.sceneTexts.set(partText, text);
            }
          }
        }
      }

      scene = scene.next;
    }
  }

  getSceneAt(nowInBeats: number): Scene {
    // If the current scene is in the future, it means the user has seeked backwards
    // In that case, start from the beginning
    // Otherwise, start from the current scene
    let scene =
      this.currentScene.start > nowInBeats
        ? this.config.initialScene
        : this.currentScene;

    while (scene.next && scene.next.start < nowInBeats) {
      scene = scene.next;
    }

    this.currentScene = scene;

    return this.currentScene;
  }

  getSequencePartAt(scene: Scene, nowInSceneBeats: number): SequencePart {
    if (!scene.sequence) {
      throw new Error(`Scene at ${scene.start} has no sequence`);
    }

    // Check if the current sequence part is still valid
    if (
      this.currentSequencePart &&
      this.currentSequencePart.start <= nowInSceneBeats &&
      this.currentSequencePart.start + this.currentSequencePart.length >
        nowInSceneBeats
    ) {
      return this.currentSequencePart;
    }

    // If not, find the current sequence part
    // If the current sequence part is in the future, it means the user has seeked backwards
    // In that case, start from the beginning
    // Otherwise, start from the current sequence part
    let part: SequencePart =
      this.currentSequencePart &&
      this.currentSequencePart.start > nowInSceneBeats
        ? scene.sequence.parts[0]
        : this.currentSequencePart ?? scene.sequence.parts[0];

    while (part && part.start + part.length < nowInSceneBeats && part.next) {
      part = part.next;
    }

    return part;
  }

  getStateAt(now: number): InstantState {
    // scene start value is in beats, so convert now from seconds to beats
    const nowInBeats = now / this.BEAT_LENGTH;
    const scene = this.getSceneAt(nowInBeats);
    const nowInSceneBeats = nowInBeats - scene.start;
    const part = this.getSequencePartAt(scene, nowInSceneBeats);

    const sceneStartTime = scene.start;
    const sceneLength = scene.length;
    // Scene progress is in beats
    const sceneProgress = nowInSceneBeats;
    // Part progress is in beats
    const partProgress = nowInSceneBeats - part.start;

    const sceneProgressFraction =
      sceneLength > 0 ? Math.min(1, sceneProgress / sceneLength) : 0;

    const partProgressFraction =
      part.length > 0 ? Math.min(1.0, partProgress / part.length) : 0;

    // Time from the start of the scene in seconds
    const sceneNow = now - sceneStartTime * this.BEAT_LENGTH;
    // Same for part / sequence
    const partNow = now - (part.start + sceneStartTime) * this.BEAT_LENGTH;

    if (!part.camera) {
      throw new Error(`No camera defined for scene at ${scene.start}`);
    }

    const transitionContext: SceneTransitionContext = {
      nowInSeconds: now,
      sceneNowInSeconds: sceneNow,
      sceneNowInBeats: nowInSceneBeats,
      sceneProgress: sceneProgressFraction,
    };

    const sequenceTransitionContext: SequenceTransitionContext = {
      sceneContext: transitionContext,
      partProgress: partProgressFraction,
    };

    const sceneVariables = computeSceneVariables(
      scene.variables,
      transitionContext,
    );
    const sceneVariables2 = computeSceneVariables(
      scene.variables2,
      transitionContext,
    );

    const camera = computeCamera(part.camera, sequenceTransitionContext);

    const fade: vec4 = part.fade
      ? part.fade(sequenceTransitionContext)
      : [0, 0, 0, 0];

    const texts: TextInstant[] = [];

    const potentialTexts: {
      text: SceneText;
      // Time in beats the text should be visible
      duration: number;
      // 0-1 fraction of the total time the text is visible and should be animated
      fraction: number;
      // Time in beats since the text was made visible
      timeSinceVisible: number;
    }[] = [
      ...(scene.text?.map((text) => {
        const textDuration = text.timing
          ? text.timing.exit - text.timing.enter
          : sceneLength;

        const fraction = clamp01(
          text.timing
            ? (nowInSceneBeats - text.timing.enter) / textDuration
            : sceneProgressFraction,
        );

        const timeSinceVisible = text.timing
          ? nowInSceneBeats - text.timing.enter
          : nowInSceneBeats;

        return {
          fraction,
          duration: textDuration,
          text,
          // If timing (enter and exit) is defined, calculate the fraction based on that
          timeSinceVisible,
        };
      }) ?? []),
      ...(part.text?.map((text) => {
        const textDuration = text.timing
          ? text.timing.exit - text.timing.enter
          : part.length;

        const fraction = clamp01(
          text.timing
            ? (partProgress - text.timing.enter) / textDuration
            : partProgressFraction,
        );

        const timeSinceVisible = text.timing
          ? partProgress - text.timing.enter
          : partProgress;

        return {
          text,
          fraction,
          duration: textDuration,
          timeSinceVisible,
        };
      }) ?? []),
    ];

    const backgroundColor: vec3 = part.backgroundColor
      ? part.backgroundColor(sequenceTransitionContext)
      : [0, 0, 0];

    for (const {
      text,
      fraction,
      timeSinceVisible,
      duration,
    } of potentialTexts) {
      if (!text) {
        continue;
      }

      if (timeSinceVisible < 0) {
        continue;
      }

      const textObj = this.sceneTexts.get(text);

      if (!textObj) {
        throw new Error(
          `Text missing for scene at ${scene.start} with text "${text.text}"`,
        );
      }
      const textPosition = computeTransition2D(text.position, fraction);

      if (text.scale) {
        textObj.transformation.setScale([text.scale[0], text.scale[1], 1]);
      }

      textObj.transformation.setTranslation(
        vec3.fromValues(textPosition[0], textPosition[1], 0.0),
      );

      // Hard coded 1 beat fade in
      let opacity = 1.0;

      if (timeSinceVisible < 1.0) {
        opacity = Math.min(1.0, timeSinceVisible / 1.0);
      }
      // Also fade out the text
      if (timeSinceVisible > duration - 1.0) {
        opacity = Math.max(0, 1.0 - (timeSinceVisible - (duration - 1.0)));
      }

      texts.push({
        text: textObj,
        opacity,
        params: text.params,
      });
    }

    const lighting = !scene.lighting
      ? LIGHTING_DEFAULT
      : computeLighting(transitionContext, scene.lighting);

    return {
      shader: part.shader ?? scene.shader,
      sceneId: part.sceneId ?? scene.sceneId ?? 0,
      now,
      sceneNow,
      nowInSceneBeats,
      name: scene.name ?? "unnamed",
      beat: nowInBeats,
      camera,
      sceneProgress: sceneProgressFraction,
      texts,
      lighting,
      sceneVariables,
      sceneVariables2,
      fade,
      backgroundColor,
      effects: scene.effects,
    };
  }
}

// Converts a linked list of scenes into a lookup table by name
// Used to implement scene replacement in hot reload
export function createSceneLookup(
  mainScene: Scene,
): Partial<Record<string, Scene>> {
  const lookup: Partial<Record<string, Scene>> = {};

  let scene: Scene | undefined = mainScene;

  while (scene) {
    if (!scene.name) {
      throw new Error(`Scene at ${scene.start} has no name`);
    }
    lookup[scene.name] = scene;
    scene = scene.next;
  }

  return lookup;
}

function compute<T, Ctx>(ctx: Ctx, value: Computed<T, Ctx>): T {
  return value instanceof Function ? value(ctx) : value;
}

function computeLighting(
  ctx: SceneTransitionContext,
  lighting: Computed<SceneLighting, SceneTransitionContext>,
): SceneLighting {
  function computeLight(light: Computed<Light, SceneTransitionContext>): Light {
    return {
      position: compute(ctx, light.position),
      intensity: compute(ctx, light.intensity),
    };
  }

  return {
    light1: computeLight(lighting.light1),
    ambient: compute(ctx, lighting.ambient),
    cameraLightIntensity: compute(ctx, lighting.cameraLightIntensity),
    fogColor: compute(ctx, lighting.fogColor),
    fogDensity: compute(ctx, lighting.fogDensity),
  };
}
